"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

/**
 * JessFrameScroll — Apple-style scroll-driven frame sequence.
 *
 * Pre-loads 300 transparent WebP frames, then renders the current
 * frame to a <canvas> on every scroll tick via GSAP ScrollTrigger.
 * Frame index is interpolated smoothly through scrollProgress so
 * the playback looks like a continuous animation (not a stutter
 * step between integers).
 *
 * - Lenis drives a single rAF loop that smooths native scroll input.
 * - ScrollTrigger.update() is wired into Lenis so the two stay in
 *   lockstep — no double-rAF, no frame drops.
 * - Canvas is fitted to its container with devicePixelRatio scaling
 *   so the WebP frames render crisp on retina + mobile.
 * - Frames are loaded in parallel during a brief "preparing" state;
 *   the section reveals once enough frames (~30%) are ready for
 *   smooth first-paint scrolling — the rest hydrate in the background.
 * - Cleanup tears down ScrollTrigger, Lenis rAF, and image refs.
 *
 * Replaces the previous video-based MeetJessBlock; same WebGL
 * context budget benefit (zero) but with true transparency and
 * scroll-controlled playback that's iOS-friendly (no MSE / no
 * codec issues — just <canvas>.drawImage).
 */

const FRAME_COUNT = 300;
const FRAME_PATH = (i: number) =>
  `/frames/jess/ezgif-frame-${String(i).padStart(3, "0")}.webp`;

// Reveal the section once this fraction of frames is ready.  Higher
// = smoother first scroll, longer wait.  30% gives the user enough
// buffer to scroll through the early section while later frames
// hydrate.
const READY_THRESHOLD = 0.3;

export default function JessFrameScroll() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);

  // ─── Preload frames ─────────────────────────────────────────────
  // Iteration 2 of the preload strategy. The previous version still
  // kicked the first 90 frames eagerly at mount and Lighthouse caught
  // it: LCP regressed from 0.6s -> 2.3s because the 90 simultaneous
  // image requests + onload callbacks competed with the hero paint
  // (element render delay 3,220ms — main thread blocked).
  //
  // New strategy: every single frame preload now runs behind
  // requestIdleCallback. The user has to scroll past the entire hero
  // (1.2 viewport heights) before they hit the pinned Jess section,
  // so giving the page a 1.5s head start is invisible to UX.
  //
  // Priority batch (0..89) fires together once idle. Tail (90..299)
  // hydrates in batches of 10, also via idle ticks.
  useEffect(() => {
    let cancelled = false;
    let loaded = 0;
    const READY_COUNT = Math.ceil(FRAME_COUNT * READY_THRESHOLD);

    const images: HTMLImageElement[] = new Array(FRAME_COUNT);
    imagesRef.current = images;

    function loadFrame(i: number, onDone?: () => void) {
      const img = new Image();
      img.src = FRAME_PATH(i + 1);
      img.decoding = "async";
      const tick = () => {
        if (cancelled) return;
        loaded += 1;
        if (loaded === READY_COUNT) setReady(true);
        onDone?.();
      };
      img.onload = tick;
      // Treat 404s as loaded so the threshold can fire — degraded > stuck.
      img.onerror = tick;
      images[i] = img;
    }

    const tail: number[] = [];
    for (let i = READY_COUNT; i < FRAME_COUNT; i++) tail.push(i);

    const hydrateTail = () => {
      if (cancelled || tail.length === 0) return;
      const batch = tail.splice(0, 10);
      let remaining = batch.length;
      batch.forEach((i) =>
        loadFrame(i, () => {
          remaining -= 1;
          if (remaining === 0) hydrateTail();
        }),
      );
    };

    const kickPriorityBatch = () => {
      if (cancelled) return;
      for (let i = 0; i < READY_COUNT; i++) loadFrame(i);
      // Once the priority batch is in flight, immediately schedule the
      // tail to start as soon as the browser is next idle.
      idleSchedule(hydrateTail, 3000);
    };

    // requestIdleCallback isn't on every browser (Safari < 16.4) — fall
    // back to setTimeout so the page paint still wins the race. Going
    // through `unknown` here because the `in` operator narrows `window`
    // to `never` in the else branch under strict TS settings.
    const w = window as unknown as {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    function idleSchedule(cb: () => void, timeout: number): number {
      if (typeof w.requestIdleCallback === "function") {
        return w.requestIdleCallback(cb, { timeout });
      }
      return window.setTimeout(cb, Math.min(timeout, 1500));
    }

    // Hold the priority batch behind a 1500ms idle deadline. On a fast
    // page the browser is idle within ~400ms of LCP; on a slow one we
    // give up at 1.5s and start fetching anyway.
    const handle = idleSchedule(kickPriorityBatch, 1500);

    return () => {
      cancelled = true;
      if (typeof handle === "number") {
        if (typeof w.cancelIdleCallback === "function") {
          w.cancelIdleCallback(handle);
        } else {
          clearTimeout(handle);
        }
      }
      imagesRef.current = [];
    };
  }, []);

  // ─── Canvas sizer ────────────────────────────────────────────────
  // Sizes the canvas backing store to devicePixelRatio × CSS size so
  // the frames render at native pixel density on retina/mobile.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // After resize redraw the current frame so the canvas doesn't
      // go blank on rotation / window resize.
      drawFrame(progress);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // progress is intentionally not in deps — resize only registers
    // once.  When progress changes the scroll handler redraws.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper that draws the frame matching a given 0..1 progress to
  // the canvas, scaling to "contain" the frame's aspect ratio.
  function drawFrame(p: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const images = imagesRef.current;
    if (images.length === 0) return;

    const idx = Math.min(
      FRAME_COUNT - 1,
      Math.max(0, Math.floor(p * (FRAME_COUNT - 1))),
    );
    const img = images[idx];
    if (!img || !img.complete || img.naturalWidth === 0) return;

    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    ctx.clearRect(0, 0, cssW, cssH);

    // object-fit: contain — preserve aspect ratio, no crop.
    const scale = Math.min(cssW / img.naturalWidth, cssH / img.naturalHeight);
    const drawW = img.naturalWidth * scale;
    const drawH = img.naturalHeight * scale;
    const dx = (cssW - drawW) / 2;
    const dy = (cssH - drawH) / 2;
    ctx.drawImage(img, dx, dy, drawW, drawH);
  }

  // ─── GSAP ScrollTrigger + Lenis smooth scroll ────────────────────
  useEffect(() => {
    if (!ready) return;
    const section = sectionRef.current;
    if (!section) return;

    gsap.registerPlugin(ScrollTrigger);

    // Lenis = wheel/touch → smoothed scroll position.  We don't pass
    // any element — Lenis attaches to <html> by default so it
    // intercepts the page's native scroll.
    const lenis = new Lenis({
      duration: 1.2, // higher = smoother but laggier; 1.2 is the
                     // sweet spot for cinematic without feeling slow
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      // smoothWheel: true is default in lenis 1.x
    });

    // Lenis emits 'scroll' on every rAF tick — feed that to GSAP
    // ScrollTrigger so the two animation loops stay synchronised.
    lenis.on("scroll", ScrollTrigger.update);

    // Single shared rAF loop drives Lenis (which drives ScrollTrigger).
    let rafId = 0;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    // GSAP ScrollTrigger pins the section, gives us a scrub'd
    // progress 0..1 that ticks every frame the user scrolls.
    const trigger = ScrollTrigger.create({
      trigger: section,
      start: "top top",
      end: "+=500%", // 5× viewport heights of scroll travel through
                     // the pinned section.  300 frames / 5 viewports
                     // = 60 frames per viewport scroll — deliberate
                     // Apple-style scrub rather than blink-and-miss.
      pin: true,
      pinSpacing: true,
      scrub: 0.5,    // 0.5s lerp smoothing on progress — extra
                     // velvet on top of Lenis's wheel smoothing
      onUpdate: (self) => {
        const p = self.progress;
        setProgress(p);
        drawFrame(p);
      },
    });

    // Force a re-measure once images may have caused layout shifts
    ScrollTrigger.refresh();

    return () => {
      cancelAnimationFrame(rafId);
      trigger.kill();
      lenis.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Fade thresholds for text — eyebrow appears early, headline in
  // mid-scroll, lead at end.  Computed from progress so they ride
  // the same scroll source as the canvas frames.
  const eyebrowOpacity = Math.max(0, Math.min(1, (progress - 0.05) / 0.1));
  const titleOpacity = Math.max(0, Math.min(1, (progress - 0.15) / 0.15));
  const leadOpacity = Math.max(0, Math.min(1, (progress - 0.35) / 0.2));

  return (
    <section
      ref={sectionRef}
      className="jess-frame-scroll"
      aria-label="Meet Jess — scroll to reveal"
    >
      <div className="jess-frame-scroll-inner">
        <canvas
          ref={canvasRef}
          className="jess-frame-canvas"
          aria-hidden="true"
        />

        {/* Reuse the liquid-glass card from MeetJessBlock — same .jess-copy
            class that exists in jess/jess.css (white-tinted backdrop-blur
            panel with rounded edges + sheen).  Keeps brand consistency
            with the /jess page and earlier Meet Jess block. */}
        <div className="jess-copy jess-frame-copy">
          <span
            className="xp-eyebrow"
            style={{
              opacity: eyebrowOpacity,
              transform: `translateY(${(1 - eyebrowOpacity) * 12}px)`,
              transition: "opacity 80ms linear, transform 80ms linear",
            }}
          >
            <span className="xp-dot" aria-hidden="true" />
            Your Dux AI assistant
          </span>
          <h1
            className="jess-title"
            style={{
              opacity: titleOpacity,
              transform: `translateY(${(1 - titleOpacity) * 24}px)`,
              transition: "opacity 100ms linear, transform 100ms linear",
            }}
          >
            Meet <em>Jess.</em>
          </h1>
          <p
            className="jess-lead"
            style={{
              opacity: leadOpacity,
              transform: `translateY(${(1 - leadOpacity) * 16}px)`,
              transition: "opacity 100ms linear, transform 100ms linear",
            }}
          >
            Round-the-clock answers on every Dux and Thermann product —
            troubleshooting, install steps, specs, error codes.
          </p>
        </div>
      </div>
    </section>
  );
}
